import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { debounceTime, distinctUntilChanged, firstValueFrom, map, Subscription } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  AddCareerRoleProfileSkillRequest,
  CareerRoleFullPictureResponse,
  CareerRoleProfileSkillMutationResponse,
  CareerRolePickerItemResponse,
  SkillCatalogItemResponse,
  SkillWithResourcesResponse
} from '../../../../models';
import { CareerRoleAdminActionsService } from './career-role-admin-actions.service';
import { TopicDependencyOption } from './add-profile-topic-dialog.component';
import {
  collectTopicOptionsFromProfile,
  getProfileSkills,
  mapSkillToAddRequest,
  mergeTopicDependencyOptions,
  resolveTopicDependsOnKeys,
  resolveTopicItems
} from './profile-skill-copy.util';

export interface ProfileSkillOption {
  id: string;
  name: string;
}

export interface AddProfileSkillDialogData {
  roleSlug: string;
  level: string;
  specializationSlug?: string;
  availableSkills?: ProfileSkillOption[];
  availableTopics?: TopicDependencyOption[];
}

/** Catalog hit or a "create new skill" pick from autocomplete. */
type SkillPick =
  | SkillCatalogItemResponse
  | { __create: true; name: string; category: string; allKnownTopics: string[] };

interface SelectedSkillRef {
  id: string | null;
  name: string;
  category: string;
  allKnownTopics: string[];
  isNew: boolean;
}

@Component({
  selector: 'app-add-profile-skill-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBarModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatIconModule,
    MatChipsModule,
    MatButtonToggleModule
  ],
  template: `
    <h2 mat-dialog-title>Add skill to {{ data.level }}{{ data.specializationSlug ? ' / ' + data.specializationSlug : '' }}</h2>
    <mat-progress-bar *ngIf="saving || searching || loadingSource" mode="indeterminate"></mat-progress-bar>
    <form [formGroup]="form" (ngSubmit)="submit()">
      <mat-dialog-content class="content">
        <mat-button-toggle-group [value]="mode" (change)="onModeChange($event.value)" class="mode-toggle">
          <mat-button-toggle value="catalog">Catalog</mat-button-toggle>
          <mat-button-toggle value="copy">Copy from role</mat-button-toggle>
        </mat-button-toggle-group>

        <ng-container *ngIf="mode === 'catalog'">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Skill name</mat-label>
          <input
            matInput
            [formControl]="searchControl"
            [matAutocomplete]="auto"
            placeholder="Search catalog or type a new skill" />
          <mat-autocomplete #auto="matAutocomplete" [displayWith]="displaySkill" (optionSelected)="onSkillPick($event.option.value)">
            <mat-option *ngFor="let skill of catalogResults" [value]="skill">{{ skill.name }} ({{ skill.category }})</mat-option>
            <mat-option *ngIf="createNewOption" [value]="createNewOption">
              Create "{{ createNewOption.name }}"
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
        <p class="hint" *ngIf="selectedSkill?.isNew">New catalog skill will be created on save.</p>
        <p class="hint" *ngIf="selectedSkill && !selectedSkill.isNew">Using existing catalog skill.</p>

        <p class="hint" *ngIf="selectedSkill">
          Catalog category: <strong>{{ selectedSkill.category || 'General' }}</strong>
          <span *ngIf="selectedSkill.isNew"> (new skills default to General; edit via skill card after add)</span>
        </p>
        </ng-container>

        <ng-container *ngIf="mode === 'copy'">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Source role</mat-label>
            <input
              matInput
              [formControl]="sourceRoleSearchControl"
              [matAutocomplete]="sourceRoleAuto"
              placeholder="Search active roles" />
            <mat-autocomplete
              #sourceRoleAuto="matAutocomplete"
              [displayWith]="displaySourceRole"
              (optionSelected)="onSourceRoleSelected($event.option.value)">
              <mat-option *ngFor="let role of sourceRoleResults" [value]="role">
                {{ role.roleName }} ({{ role.roleSlug }})
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Source level</mat-label>
            <mat-select formControlName="sourceLevel" (selectionChange)="onSourceProfileContextChange()">
              <mat-option *ngFor="let level of sourceLevels" [value]="level">{{ level }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full-width" *ngIf="data.specializationSlug">
            <mat-label>Source specialization</mat-label>
            <mat-select formControlName="sourceSpecializationSlug" (selectionChange)="onSourceProfileContextChange()">
              <mat-option *ngFor="let spec of sourceSpecializations" [value]="spec.slug">
                {{ spec.name }} ({{ spec.slug }})
              </mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Source skill</mat-label>
            <mat-select formControlName="sourceSkillId" (selectionChange)="onSourceSkillSelected()">
              <mat-option *ngFor="let skill of sourceProfileSkills" [value]="skill.skill.id">
                {{ skill.skill.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>
          <p class="hint" *ngIf="copySourceHint">{{ copySourceHint }}</p>
        </ng-container>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Depends on skills</mat-label>
          <mat-select formControlName="dependsOnSkillIds" multiple>
            <mat-option *ngFor="let skill of dependableSkillsList" [value]="skill.id">{{ skill.name }}</mat-option>
          </mat-select>
        </mat-form-field>

        <div formArrayName="topics">
          <div class="topic-block" *ngFor="let topic of topics.controls; let i = index" [formGroupName]="i">
            <div class="topic-row">
              <mat-form-field appearance="outline" class="topic-name">
                <mat-label>Topic</mat-label>
                <input matInput formControlName="name" />
              </mat-form-field>
              <mat-form-field appearance="outline" class="topic-prof">
                <mat-label>Proficiency</mat-label>
                <mat-select formControlName="requiredProficiency">
                  <mat-option value="beginner">beginner</mat-option>
                  <mat-option value="intermediate">intermediate</mat-option>
                  <mat-option value="advanced">advanced</mat-option>
                  <mat-option value="expert">expert</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="topic-imp">
                <mat-label>Importance</mat-label>
                <input matInput type="number" min="0" max="1" step="0.05" formControlName="importance" />
              </mat-form-field>
              <button mat-icon-button type="button" (click)="removeTopic(i)" [disabled]="topics.length === 1" title="Remove topic">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Depends on topics</mat-label>
              <mat-select formControlName="dependsOnKeys" multiple>
                <mat-option *ngFor="let opt of topicDependencyOptions" [value]="opt.label">{{ opt.label }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>
        <button mat-stroked-button type="button" (click)="addTopic()">Add topic</button>
        <p class="error" *ngIf="error">{{ error }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="close()" [disabled]="saving">Cancel</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || !canSubmit || saving">Add</button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [
    `
      .content { min-width: 480px; display: flex; flex-direction: column; gap: 0.5rem; }
      .full-width { width: 100%; }
      .topic-block { margin-bottom: 0.5rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e2e8f0; }
      .topic-row { display: flex; gap: 0.5rem; align-items: flex-start; flex-wrap: wrap; }
      .topic-name { flex: 1; min-width: 140px; }
      .topic-prof { width: 140px; }
      .topic-imp { width: 110px; }
      .hint { margin: 0; color: #64748b; font-size: 0.85rem; }
      .error { color: #b00020; }
      .mode-toggle { margin-bottom: 0.5rem; }
    `
  ]
})
export class AddProfileSkillDialogComponent implements OnDestroy {
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly dialogRef = inject(MatDialogRef<AddProfileSkillDialogComponent, CareerRoleProfileSkillMutationResponse | undefined>);
  readonly data = inject<AddProfileSkillDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private topicsSubscription?: Subscription;
  saving = false;
  searching = false;
  loadingSource = false;
  error = '';
  mode: 'catalog' | 'copy' = 'catalog';
  copySourceHint = '';
  catalogResults: SkillCatalogItemResponse[] = [];
  createNewOption: { __create: true; name: string; category: string; allKnownTopics: string[] } | null = null;
  selectedSkill: SelectedSkillRef | null = null;
  sourceRoleResults: CareerRolePickerItemResponse[] = [];
  sourceLevels: string[] = [];
  sourceSpecializations: { slug: string; name: string }[] = [];
  sourceProfileSkills: SkillWithResourcesResponse[] = [];
  private sourcePicture: CareerRoleFullPictureResponse | null = null;
  /** Stable lists for mat-select (never allocate in the template). */
  dependableSkillsList: ProfileSkillOption[] = [...(this.data.availableSkills ?? [])];
  topicDependencyOptions: TopicDependencyOption[] = [...(this.data.availableTopics ?? [])];
  private readonly profileTopicOptions: TopicDependencyOption[] = [...(this.data.availableTopics ?? [])];

  readonly searchControl = this.fb.nonNullable.control<string | SkillPick>('');
  readonly sourceRoleSearchControl = this.fb.nonNullable.control<string | CareerRolePickerItemResponse>('');
  readonly form = this.fb.nonNullable.group({
    dependsOnSkillIds: this.fb.nonNullable.control<string[]>([]),
    sourceLevel: [''],
    sourceSpecializationSlug: [''],
    sourceSkillId: [''],
    topics: this.fb.array([this.createTopicGroup()])
  });

  get canSubmit(): boolean {
    return !!this.resolveSkillForSubmit();
  }

  constructor() {
    this.searchControl.valueChanges.pipe(debounceTime(250)).subscribe((value) => {
      if (typeof value === 'string') {
        this.onSearchTextChanged(value);
        void this.searchCatalog(value);
      }
    });
    this.sourceRoleSearchControl.valueChanges.pipe(debounceTime(250)).subscribe((value) => {
      if (typeof value === 'string') {
        void this.searchSourceRoles(value);
      }
    });
    // Only when topic names change — not when dependsOnKeys changes (avoids mat-select CD loops).
    this.topicsSubscription = this.topics.valueChanges
      .pipe(
        debounceTime(100),
        map(() =>
          this.topics
            .getRawValue()
            .map((t) => ((t.name as string | undefined)?.trim() ?? ''))
            .join('\u0001')
        ),
        distinctUntilChanged()
      )
      .subscribe(() => this.refreshTopicDependencyOptions());
  }

  ngOnDestroy(): void {
    this.topicsSubscription?.unsubscribe();
  }

  get topics(): FormArray {
    return this.form.get('topics') as FormArray;
  }

  displaySkill(skill: SkillPick | string | null): string {
    if (!skill || typeof skill === 'string') {
      return typeof skill === 'string' ? skill : '';
    }
    return skill.name;
  }

  displaySourceRole(role: CareerRolePickerItemResponse | string | null): string {
    if (!role || typeof role === 'string') {
      return typeof role === 'string' ? role : '';
    }
    return `${role.roleName} (${role.roleSlug})`;
  }

  onModeChange(mode: 'catalog' | 'copy' | null): void {
    if (!mode) {
      return;
    }
    this.mode = mode;
    this.error = '';
    if (mode === 'copy' && !this.sourceRoleResults.length) {
      void this.searchSourceRoles('');
    }
  }

  async onSourceRoleSelected(role: CareerRolePickerItemResponse): Promise<void> {
    this.loadingSource = true;
    this.error = '';
    this.sourcePicture = null;
    this.sourceProfileSkills = [];
    this.form.patchValue({ sourceLevel: '', sourceSpecializationSlug: '', sourceSkillId: '' });
    try {
      this.sourcePicture = await firstValueFrom(this.api.getFullPicture(role.roleSlug));
      this.sourceLevels = this.sourcePicture.seniorityProfiles.map((p) => p.level);
      this.sourceSpecializations = this.sourcePicture.specializations.map((s) => ({
        slug: s.slug,
        name: s.name
      }));
      if (this.sourceLevels.length === 1) {
        this.form.patchValue({ sourceLevel: this.sourceLevels[0] });
      }
      if (this.data.specializationSlug && this.sourceSpecializations.length === 1) {
        this.form.patchValue({ sourceSpecializationSlug: this.sourceSpecializations[0].slug });
      }
      this.onSourceProfileContextChange();
    } catch (error) {
      this.error = this.actions.errorMessage(error);
    } finally {
      this.loadingSource = false;
    }
  }

  onSourceProfileContextChange(): void {
    if (!this.sourcePicture) {
      this.sourceProfileSkills = [];
      return;
    }
    const level = this.form.value.sourceLevel ?? '';
    const specSlug = this.data.specializationSlug ? this.form.value.sourceSpecializationSlug ?? '' : undefined;
    if (!level || (this.data.specializationSlug && !specSlug)) {
      this.sourceProfileSkills = [];
      this.form.patchValue({ sourceSkillId: '' });
      return;
    }
    this.sourceProfileSkills = getProfileSkills(this.sourcePicture, level, specSlug || undefined);
    this.form.patchValue({ sourceSkillId: '' });
    this.selectedSkill = null;
    this.copySourceHint = '';
  }

  onSourceSkillSelected(): void {
    const skillId = this.form.value.sourceSkillId ?? '';
    const skill = this.sourceProfileSkills.find((s) => s.skill.id === skillId);
    if (!skill) {
      return;
    }
    void this.applyCopyFromSource(skill);
  }

  private async applyCopyFromSource(skill: SkillWithResourcesResponse): Promise<void> {
    const request = mapSkillToAddRequest(skill);
    if (!request) {
      this.error = `${skill.skill.name} has no topics to copy.`;
      return;
    }
    const level = this.form.value.sourceLevel ?? '';
    const specSlug = this.data.specializationSlug ? this.form.value.sourceSpecializationSlug ?? '' : '';
    const roleLabel =
      typeof this.sourceRoleSearchControl.value === 'object' && this.sourceRoleSearchControl.value
        ? this.sourceRoleSearchControl.value.roleSlug
        : 'source role';
    this.copySourceHint = `Copied from ${roleLabel} / ${level}${specSlug ? ' / ' + specSlug : ''} — edit before save`;

    this.selectedSkill = {
      id: skill.skill.id,
      name: skill.skill.name,
      category: skill.category || 'General',
      allKnownTopics: [],
      isNew: false
    };
    this.dependableSkillsList = (this.data.availableSkills ?? []).filter((s) => s.id !== skill.skill.id);
    this.topicDependencyOptions = mergeTopicDependencyOptions(
      this.profileTopicOptions,
      collectTopicOptionsFromProfile(this.sourceProfileSkills)
    );

    this.form.patchValue({
      dependsOnSkillIds: (request.dependsOn ?? []).map((d) => d.skillId)
    });

    this.topics.clear();
    for (const topic of request.topics) {
      const sourceTopic = resolveTopicItems(skill).find(
        (t) => t.name.localeCompare(topic.name, undefined, { sensitivity: 'accent' }) === 0
      );
      const dependsOnKeys = sourceTopic
        ? resolveTopicDependsOnKeys(
            sourceTopic,
            this.topicDependencyOptions,
            skill.skill.id,
            skill.skill.name
          )
        : [];
      this.topics.push(this.createTopicGroup(topic.name, topic.requiredProficiency, topic.importance, dependsOnKeys));
    }
    if (!this.topics.length) {
      this.topics.push(this.createTopicGroup());
    }
    this.refreshTopicDependencyOptions();
  }

  onSkillPick(pick: SkillPick): void {
    if ('__create' in pick && pick.__create) {
      this.applySelectedSkill({
        id: null,
        name: pick.name.trim(),
        category: 'General',
        allKnownTopics: [],
        isNew: true
      });
      return;
    }

    const skill = pick as SkillCatalogItemResponse;
    this.applySelectedSkill({
      id: skill.id,
      name: skill.name,
      category: skill.category || '',
      allKnownTopics: skill.allKnownTopics ?? [],
      isNew: false
    });
  }

  private applySelectedSkill(skill: SelectedSkillRef): void {
    this.selectedSkill = skill;
    this.dependableSkillsList = (this.data.availableSkills ?? []).filter((s) => s.id !== skill.id);
    if (skill.allKnownTopics.length && this.topics.length === 1 && !this.topics.at(0).value.name) {
      this.topics.clear();
      for (const topic of skill.allKnownTopics.slice(0, 5)) {
        this.topics.push(this.createTopicGroup(topic));
      }
    }
    this.refreshTopicDependencyOptions();
  }

  private onSearchTextChanged(text: string): void {
    const query = text.trim();
    // Typing clears a prior catalog selection unless it still matches exactly.
    if (
      this.selectedSkill &&
      !this.selectedSkill.isNew &&
      this.selectedSkill.name.localeCompare(query, undefined, { sensitivity: 'accent' }) !== 0
    ) {
      this.selectedSkill = null;
    }
    if (this.selectedSkill?.isNew && this.selectedSkill.name !== query) {
      this.selectedSkill = null;
    }
  }

  addTopic(): void {
    this.topics.push(this.createTopicGroup());
    this.refreshTopicDependencyOptions();
  }

  removeTopic(index: number): void {
    if (this.topics.length > 1) {
      this.topics.removeAt(index);
      this.refreshTopicDependencyOptions();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    const skill = this.resolveSkillForSubmit();
    if (!skill || this.form.invalid || this.saving) {
      return;
    }
    this.saving = true;
    this.error = '';
    const dependsOnSkillIds = this.form.value.dependsOnSkillIds ?? [];
    const request: AddCareerRoleProfileSkillRequest = {
      skillId: skill.isNew ? undefined : skill.id ?? undefined,
      skillName: skill.isNew ? skill.name : undefined,
      dependsOn: dependsOnSkillIds.map((skillId) => ({ skillId, kind: 'hard' })),
      topics: this.topics.getRawValue().map((t) => {
        const keys = (t.dependsOnKeys as string[]) ?? [];
        const topicName = (t.name as string).trim();
        return {
          name: topicName,
          requiredProficiency: t.requiredProficiency as string,
          importance: Number(t.importance ?? 0.5),
          dependsOn: keys
            .map((key) => this.topicDependencyOptions.find((o) => o.label === key))
            .filter((o): o is TopicDependencyOption => !!o && o.topicName !== topicName)
            .map((o) => ({
              skillId: !o.skillId || o.skillId === skill.id ? null : o.skillId,
              topicName: o.topicName,
              kind: 'hard'
            }))
        };
      })
    };
    try {
      const result = this.data.specializationSlug
        ? await firstValueFrom(
            this.api.addCareerRoleFocusSkill(
              this.data.roleSlug,
              this.data.specializationSlug,
              this.data.level,
              request
            )
          )
        : await firstValueFrom(this.api.addCareerRoleCoreSkill(this.data.roleSlug, this.data.level, request));
      this.dialogRef.close(result);
    } catch (error) {
      this.error = this.actions.errorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  private createTopicGroup(
    name = '',
    requiredProficiency = 'intermediate',
    importance = 0.5,
    dependsOnKeys: string[] = []
  ) {
    return this.fb.nonNullable.group({
      name: [name, [Validators.required, Validators.minLength(1)]],
      requiredProficiency: [requiredProficiency, Validators.required],
      importance: [importance, [Validators.required, Validators.min(0), Validators.max(1)]],
      dependsOnKeys: this.fb.nonNullable.control<string[]>(dependsOnKeys)
    });
  }

  private resolveSkillForSubmit(): SelectedSkillRef | null {
    if (this.selectedSkill?.name.trim()) {
      return this.selectedSkill;
    }
    if (this.mode === 'copy') {
      return null;
    }
    const typed = this.typedSkillName();
    if (typed.length < 2) {
      return null;
    }
    return {
      id: null,
      name: typed,
      category: 'General',
      allKnownTopics: [],
      isNew: true
    };
  }

  private typedSkillName(): string {
    const value = this.searchControl.value;
    if (typeof value === 'string') {
      return value.trim();
    }
    if (value && typeof value === 'object' && 'name' in value) {
      return String(value.name ?? '').trim();
    }
    return '';
  }

  /** Rebuild once on form changes — never from the template (avoids CD loops with mat-select). */
  private refreshTopicDependencyOptions(): void {
    const skillName = this.selectedSkill?.name ?? (this.typedSkillName() || 'This skill');
    const skillId = this.selectedSkill?.id ?? '';
    const options = [...this.profileTopicOptions];
    const seen = new Set(options.map((o) => o.label));
    for (const row of this.topics.getRawValue()) {
      const name = (row.name as string | undefined)?.trim() ?? '';
      if (!name) {
        continue;
      }
      const label = `${skillName} / ${name}`;
      if (seen.has(label)) {
        continue;
      }
      seen.add(label);
      options.push({ skillId, skillName, topicName: name, label });
    }
    this.topicDependencyOptions = options;
  }

  private async searchCatalog(text: string): Promise<void> {
    const query = text.trim();
    if (query.length < 2) {
      this.catalogResults = [];
      this.createNewOption = null;
      return;
    }
    this.searching = true;
    try {
      const page = await firstValueFrom(this.api.listSkillCatalog(1, 20, query));
      this.catalogResults = page.data ?? [];
      const exactMatch = this.catalogResults.some(
        (s) => s.name.localeCompare(query, undefined, { sensitivity: 'accent' }) === 0
      );
      this.createNewOption = exactMatch
        ? null
        : { __create: true, name: query, category: '', allKnownTopics: [] };
    } catch {
      this.catalogResults = [];
      this.createNewOption = { __create: true, name: query, category: '', allKnownTopics: [] };
    } finally {
      this.searching = false;
    }
  }

  private async searchSourceRoles(text: string): Promise<void> {
    this.loadingSource = true;
    try {
      const page = await firstValueFrom(this.api.listCareerRolesPicker(1, 30, text, true));
      this.sourceRoleResults = page.data ?? [];
    } catch {
      this.sourceRoleResults = [];
    } finally {
      this.loadingSource = false;
    }
  }
}